import React, { FC, JSX, useEffect, useState } from "react";

import { ServicingProvider } from "@app/projects/results/servicing-provider";
import { NetworkServicingProviderTerminationModel } from "@app/provider-terminations/networkServicingProviderTerminationModel";
import { PciDashboardTerminationDetails } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import ProviderTerminationService from "@app/provider-terminations/providerTerminationService";
import UpdateProviderTermination from "@app/provider-terminations/updateProviderTermination";
import { QESAlert, QESButton, QESRadioButton, QESSelect, QESTextArea } from "@app/shared/qes-standards";
import { Roles, UserContextProps, withUserContext } from "@app/user";
import { QESCheckbox } from "../../shared/qes-standards/forms/Checkbox";
import { Features } from "@app/feature-flags";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { FlyoutContextOutgoingProps, FlyoutFooter, withFlyoutContext } from "@app/shared/Flyouts";

export enum TerminatePanelMode {
  Create = "create",
  Edit = "edit",
  View = "View",
}

export interface TerminateProps {
  onSubmit: () => void;
  projectId: number;
  specialtyGroupCode?: string;
  selectedProviders?: ServicingProvider[];
  terminatePanelMode: TerminatePanelMode;
  termination?: NetworkServicingProviderTerminationModel | PciDashboardTerminationDetails;
  npis?: string[];
}

const getDefaultIdentifier = (selectedProviders: ServicingProvider[], termination: NetworkServicingProviderTerminationModel | PciDashboardTerminationDetails, npis: string[]): string => {
  if (termination) {
    if (termination.terminateByNPI && termination.terminateByTIN) {
      return "npitin";
    }
    if (termination.terminateByTIN) {
      return "tin";
    }
    if (termination.terminateByNPI) {
      return "npi";
    }
    if (!termination.terminateByNPI && !termination.terminateByTIN) {
      // Not terminated by NPI or TIN, so terminate by name
      return "name";
    }
  }

  if (selectedProviders?.every((provider) => !!provider.npi)) {
    return "npi";
  }
  if (selectedProviders?.every((provider) => !!provider.tin)) {
    return "tin";
  }
  if (selectedProviders?.every((provider) => !provider.npi && !provider.tin)) {
    return "name";
  }

  if (selectedProviders?.some((provider) => !provider.npi && !provider.tin)) {
    if (selectedProviders?.some((provider) => !!provider.npi)) {
      return "npi";
    }
    else if (selectedProviders?.some((provider) => !!provider.tin)) {
      return "tin";
    }
    else {
      // All providers are missing NPI and TIN, so terminate by name
      return "name";
    }
  }

  if (npis?.length > 0) {
    return "npi";
  }

  return null;
};

const isTerminationFromPciDashboard = (obj: PciDashboardTerminationDetails | NetworkServicingProviderTerminationModel
): obj is PciDashboardTerminationDetails => {
  return true;
}

const maxLength = 1000;

type TerminatePanelProps = TerminateProps & UserContextProps & FlyoutContextOutgoingProps &
{
  setAllSpecialtiesInParent?: (allSpecialties: boolean) => void;
  setAllLocationsInParent?: (allLocations: boolean) => void;
  setIdentifierInParent?: (identifier: string) => void;
};

const TerminateProvidersPanel: FC<React.PropsWithChildren<TerminatePanelProps>> = (props: TerminatePanelProps) => {
  const existingTermination = props.termination;
  const createMode = props.terminatePanelMode === TerminatePanelMode.Create;
  const viewMode = props.terminatePanelMode === TerminatePanelMode.View;
  const editMode = props.terminatePanelMode === TerminatePanelMode.Edit;
  const providerTerminationService = new ProviderTerminationService();
  const checkboxDisabled = viewMode || editMode;
  const userIsTermAdmin = props.user.roles.includes(Roles.TERM_PROVIDER_ADMIN);
  const userIsTermViewer = props.user.roles.includes(Roles.TERM_PROVIDER_VIEWER);
  const terminatingByNpi = React.useContext(FeatureFlagContext).enabledFeatureFlags.has(Features.PCI_DASHBOARD_TERMINATE_GHOST_PROVIDERS) && (props?.npis?.length > 0);
  const terminateByNameEnabled = React.useContext(FeatureFlagContext).enabledFeatureFlags.has(Features.TERMINATE_BY_NAME);
  const updatingFromPciDashboard = React.useContext(FeatureFlagContext).enabledFeatureFlags.has(Features.PCI_DASHBOARD_TERMINATE_GHOST_PROVIDERS) && (isTerminationFromPciDashboard(props.termination))

  const [reasons, setReasons] = useState<string[]>([]);
  const [reason, setReason] = useState(existingTermination && existingTermination.reasonName || "");
  const [comment, setComment] = useState(existingTermination && existingTermination.reasonDescription || "");
  const [allSpecialties, setAllSpecialties] = useState(existingTermination && existingTermination.allSpecialties || false);
  const [allLocations, setAllLocations] = useState(existingTermination && existingTermination.allLocations || false);
  const [identifier, setIdentifier] = useState(getDefaultIdentifier(props?.selectedProviders, existingTermination, props?.npis));
  const [error, setError] = useState<JSX.Element>(null);

  useEffect(() => {
    if (props.user.roles.indexOf(Roles.TERM_PROVIDER_ADMIN) > -1) {
      providerTerminationService.getProviderTerminationReasons()
        .then((result) => {
          setReasons(result);
        }, () => {
          setError(<QESAlert type="danger">Something went wrong on our end.</QESAlert>);
        });
    } else {
      setReasons([]);
    }

    if (props.setAllSpecialtiesInParent)
      props.setAllSpecialtiesInParent(allSpecialties);

    if (props.setAllLocationsInParent)
      props.setAllLocationsInParent(allLocations);

    if (props.setIdentifierInParent)
      props.setIdentifierInParent(identifier);
  }, []);

  useEffect(() => {
    if (props.setIdentifierInParent)
      props.setIdentifierInParent(identifier);
  }, [identifier]);

  useEffect(() => {
    if (props.setAllSpecialtiesInParent)
      props.setAllSpecialtiesInParent(allSpecialties);
  }, [allSpecialties]);

  useEffect(() => {
    if (props.setAllLocationsInParent)
      props.setAllLocationsInParent(allLocations);
  }, [allLocations]);

  const tinViewer = props.user.roles.indexOf(Roles.TIN_VIEWER) > -1;

  let showNpi = false;
  let showTin = false;
  let terminateByName = false;
  let anyNameTerminations = false;

  if (props.selectedProviders) {
    showNpi = props.selectedProviders?.every((provider) => !!provider.npi);
    showTin = tinViewer && props.selectedProviders?.every((provider) => !!provider.tin);
    anyNameTerminations = terminateByNameEnabled && props.selectedProviders?.some((provider) => !provider.npi && (!tinViewer || !provider.tin));
  }
  if (terminatingByNpi) {
    showNpi = true;
  }

  if (updatingFromPciDashboard) {
    showNpi = true;
  }

  terminateByName = terminateByNameEnabled && identifier.includes("name");

  const handleSubmit = async () => {
    if ((!identifier.includes("npi") && !identifier.includes("tin") && !identifier.includes("name"))
      || (reason === "Other" && comment.length === 0)) {
      return;
    }
    if (editMode) {
      await providerTerminationService.updateProviderTermination(props.projectId, { providerTerminationProjectId: existingTermination.providerTerminationProjectId, reasonDescription: comment, reasonName: reason } as UpdateProviderTermination);
    }

    if (createMode && props.selectedProviders) {
      const submittedProviders = [...props.selectedProviders];

      await providerTerminationService.createProviderTerminations(props.projectId,
        identifier.includes("npi"),
        identifier.includes("tin") && tinViewer,
        submittedProviders.map((p) => p.providerId),
        reason,
        comment.substring(0, maxLength),
        allSpecialties,
        allLocations,
        props?.specialtyGroupCode).then((): void => undefined, (reject) => setError(<QESAlert type="danger">{reject.responseText}</QESAlert>))
    }

    if (createMode && terminatingByNpi) {
      await providerTerminationService.createGhostProviderTerminations(props.projectId,
        true,
        reason,
        comment.substring(0, maxLength),
        props.npis).then((): void => undefined, (reject) => setError(<QESAlert type="danger">{reject.responseText}</QESAlert>))
    }

    props.onSubmit();
    props.handleCloseFlyout();
  };

  let noMatchingCriteria = "All selected providers must have NPIs or all selected providers must have TINs.";
  if (!tinViewer) {
    noMatchingCriteria = "All selected providers must have NPIs.";
  }

  if (!showNpi && !showTin && !terminateByName) {
    return (
      <>
        <p className="text-danger"><i className="fa fa-exclamation-triangle"> {noMatchingCriteria}</i></p>
      </>
    );
  }

  return (
    <>
      {error ? error :
        (
          <>
            <div className="row mb-4">
              <div className="col-9">
                <QESCheckbox
                  label="All Locations"
                  testId="termination-flyout__all-locations"
                  checked={allLocations || terminatingByNpi}
                  disabled={checkboxDisabled || terminatingByNpi || anyNameTerminations}
                  onChange={(e) => setAllLocations(e.target.checked)}
                />
              </div>

              <div className="col-9">
                <QESCheckbox
                  label="All Specialties"
                  checked={allSpecialties || terminatingByNpi}
                  disabled={checkboxDisabled || terminatingByNpi || anyNameTerminations}
                  onChange={(e) => setAllSpecialties(e.target.checked)}
                  testId="termination-flyout__all-specialties"
                />
              </div>
            </div>

            {!terminateByName &&
              (<div>
                <div className="row">
                  <label className="col-2 label-s d-block mb-2" htmlFor="reason">Recording Criteria:</label>
                </div>
                <div className="row">
                  <div className="col-9 ml-4">
                    {showNpi && (<div>
                      <QESRadioButton
                        label="NPI"
                        checked={identifier === "npi" || terminatingByNpi}
                        disabled={checkboxDisabled || terminatingByNpi}
                        name="identifier"
                        onChange={(e) => setIdentifier(e.target.value)}
                        value="npi"
                        testId="termination-flyout__by-npi"
                      />
                    </div>)}

                    {showTin && (<div>
                      <QESRadioButton
                        label="TIN"
                        checked={identifier === "tin"}
                        disabled={checkboxDisabled}
                        name="identifier"
                        onChange={(e) => setIdentifier(e.target.value)}
                        value="tin"
                        testId="termination-flyout__by-tin"
                      />
                    </div>)}

                    {showNpi && showTin && (<div>
                      <QESRadioButton
                        label="NPI and TIN"
                        checked={identifier === "npitin"}
                        disabled={checkboxDisabled}
                        name="identifier"
                        onChange={(e) => setIdentifier(e.target.value)}
                        value="npitin"
                        testId="termination-flyout__by-npi-and-tin"
                      />
                    </div>)}
                  </div>
                </div>
              </div>)}
            <div className="row mb-3 mt-4">
              <div className="col-12">
                <p>The Reason and Comment entered below will be visible to any other users within your organization with the Termination Viewer or Termination Provider Admin role.</p>
              </div>
            </div>
            <div className="row mb-3 col-6">
              <QESSelect
                label="Reason"
                disabled={viewMode || (!userIsTermAdmin && userIsTermViewer)}
                onChange={(e) => setReason(e.target.value)}
                value={reason}
                placeholder=""
                testId="termination-flyout__reason"
                required={true}
                isValid={!!reason}
              >
                {reasons.map((o) => <option key={o} value={o}>{o}</option>)}
              </QESSelect>
            </div>

            <div className="row mb-3">
              <QESTextArea
                containerClassName="col-6"
                label="Comment"
                testId="termination-flyout__comment"
                disabled={viewMode}
                maxLength={maxLength}
                onChange={(e) => setComment(e.target.value)}
                value={comment}
                required={reason === "Other"}
                isValid={!!comment}
              />
            </div>

            {!viewMode && (
              <FlyoutFooter
                submitButton={<QESButton qesType="primary" qesTestId="termination-flyout__submit" onClick={handleSubmit}
                  disabled={!reason || !identifier || (reason === "Other" && !comment)}>Submit</QESButton>}
              />
            )}
          </>
        )
      }
    </>
  );
};
const WrappedTerminateProvidersPanel = withFlyoutContext(withUserContext(TerminateProvidersPanel));
export { WrappedTerminateProvidersPanel, getDefaultIdentifier };
